import { useEffect } from 'react';

const SITE_ORIGIN = 'https://pdf-to-text.com';

/** Head tags a page can override; each entry is [selector, attribute]. */
const TAGS = {
  description: ['meta[name="description"]', 'content'],
  ogTitle: ['meta[property="og:title"]', 'content'],
  ogDescription: ['meta[property="og:description"]', 'content'],
  ogUrl: ['meta[property="og:url"]', 'content'],
  ogType: ['meta[property="og:type"]', 'content'],
  twitterTitle: ['meta[property="twitter:title"]', 'content'],
  twitterDescription: ['meta[property="twitter:description"]', 'content'],
  twitterUrl: ['meta[property="twitter:url"]', 'content'],
  canonical: ['link[rel="canonical"]', 'href'],
} as const;

type TagName = keyof typeof TAGS;

const read = (name: TagName) => document.querySelector(TAGS[name][0])?.getAttribute(TAGS[name][1]) ?? null;

type Snapshot = { title: string } & Record<TagName, string | null>;

/** Site-wide values from index.html, captured on first use and restored when a page unmounts. */
let defaults: Snapshot | null = null;
const siteDefaults = (): Snapshot =>
  (defaults ??= {
    title: document.title,
    ...(Object.fromEntries((Object.keys(TAGS) as TagName[]).map((n) => [n, read(n)])) as Record<TagName, string | null>),
  });

const write = (name: TagName, value: string | null | undefined) => {
  if (value == null) return;
  const [selector, attr] = TAGS[name];
  let el = document.querySelector(selector);
  if (!el && name === 'canonical') {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el?.setAttribute(attr, value);
};

interface DocumentMetaOptions {
  title: string;
  description?: string;
  /** Site-relative path (e.g. "/text-editor") to use as this page's canonical URL. */
  canonicalPath?: string;
  /** Open Graph type, e.g. "article" for guides. */
  ogType?: 'website' | 'article';
}

/**
 * Sets the title, meta description, canonical URL and the matching Open
 * Graph / Twitter tags for the lifetime of the mounted route, restoring the
 * site defaults on unmount. Search engines that render JavaScript (Google,
 * Bing) see these; crawlers that don't (most social preview bots) see the
 * site-wide tags in index.html.
 */
export const useDocumentMeta = ({ title, description, canonicalPath, ogType }: DocumentMetaOptions): void => {
  useEffect(() => {
    const site = siteDefaults();
    const url = canonicalPath ? `${SITE_ORIGIN}${canonicalPath}` : undefined;
    document.title = title;
    write('ogTitle', title);
    write('twitterTitle', title);
    write('description', description);
    write('ogDescription', description);
    write('twitterDescription', description);
    write('canonical', url);
    write('ogUrl', url);
    write('twitterUrl', url);
    write('ogType', ogType);
    return () => {
      document.title = site.title;
      (Object.keys(TAGS) as TagName[]).forEach((name) => write(name, site[name]));
    };
  }, [title, description, canonicalPath, ogType]);
};
